import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import { isLocalEnvironment } from '../config/admin';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  role: string | null;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

/** Fetch the user's role from the admin API — token passed directly to avoid
 *  timing issues where auth.currentUser may not be set yet in the interceptor. */
async function fetchRole(uid: string, idToken?: string): Promise<string | null> {
  try {
    const headers: Record<string, string> = {};
    if (idToken) headers['Authorization'] = `Bearer ${idToken}`;
    const targetUrl = `${import.meta.env.VITE_API_BASE_URL}/admin/users/${uid}`;
    const response = await fetch(targetUrl, { headers });
    if (!response.ok) return null;
    const json = await response.json() as { data?: { role?: string } };
    return json?.data?.role ?? null;
  } catch {
    return null;
  }
}


export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // In local dev, skip auth entirely
    if (isLocalEnvironment()) {
      setIsLoading(false);
      return;
    }

    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        const idToken = await firebaseUser.getIdToken();
        const r = await fetchRole(firebaseUser.uid, idToken);
        // Sign out immediately if user has no admin role
        if (!r || !r.includes('admin')) {
          await signOut(auth);
          setRole(null);
        } else {
          setRole(r);
        }
      } else {
        setRole(null);
      }
      setIsLoading(false);
    });

    return unsub;
  }, []);

  const loginWithGoogle = async () => {
    // signInWithPopup — works on Cloudflare Pages (no /__/auth/handler needed)
    await signInWithPopup(auth, googleProvider);
    // onAuthStateChanged above handles role verification
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
    setRole(null);
  };

  const isAuthenticated = isLocalEnvironment() || (user !== null && role !== null && role.includes('admin'));

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, user, role, loginWithGoogle, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
